import React, { useEffect, useState } from 'react';
import { Badge, Button, Callout, Dialog, Flex, Table, Text } from '@radix-ui/themes';
import { ExclamationTriangleIcon } from '@radix-ui/react-icons';
import { patientApi } from '../services/api';
import type { SmsUndeliverablePatient } from '../services/api';

interface SmsUndeliverableBannerProps {
  /** The clinic selected in the org switcher; omitted / 'ALL' = every clinic the user sees. */
  organizationId?: string | null;
  /** Change to force a re-fetch (e.g. on return to the patient list). */
  refreshKey?: number;
  onOpenPatient: (patient: SmsUndeliverablePatient) => void;
}

const formatSince = (iso?: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
};

/**
 * PHA-1187: the clinic's work list of patients whose phone number Twilio reported as unable
 * to receive SMS (landline, unreachable or inactive). Their survey texts are paused (PHA-1025),
 * so without a working mobile number — or a voice call — they produce no survey responses,
 * which is billing exposure. Renders nothing when the list is empty or cannot be loaded.
 */
const SmsUndeliverableBanner: React.FC<SmsUndeliverableBannerProps> = ({
  organizationId,
  refreshKey,
  onOpenPatient,
}) => {
  const [patients, setPatients] = useState<SmsUndeliverablePatient[]>([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    patientApi
      .getSmsUndeliverablePatients(organizationId || undefined)
      .then((result) => {
        if (!cancelled) setPatients(result?.patients ?? []);
      })
      .catch(() => {
        // Advisory only: never block the patient list on this.
        if (!cancelled) setPatients([]);
      });
    return () => {
      cancelled = true;
    };
  }, [organizationId, refreshKey]);

  if (patients.length === 0) return null;

  const viaVoice = patients.filter((p) => p.surveys_route_to_voice).length;
  const noun = patients.length === 1 ? 'patient' : 'patients';

  return (
    <>
      <Callout.Root color="orange" className="mb-4" data-testid="sms-undeliverable-banner">
        <Callout.Icon>
          <ExclamationTriangleIcon />
        </Callout.Icon>
        <Callout.Text>
          {/* A span, not <Flex>: Callout.Text renders a <p>, which cannot hold a <div>. */}
          <span style={{ display: 'inline-flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <span>
              <strong>
                {patients.length} {noun} cannot receive survey texts.
              </strong>{' '}
              Their phone number was rejected as a landline or unreachable, so texts are paused.
              {viaVoice > 0 && ` ${viaVoice} will be surveyed by Voice AI instead.`}
            </span>
            <Button size="1" variant="soft" color="orange" onClick={() => setOpen(true)}>
              Review list
            </Button>
          </span>
        </Callout.Text>
      </Callout.Root>

      <Dialog.Root open={open} onOpenChange={setOpen}>
        <Dialog.Content style={{ maxWidth: 900 }}>
          <Dialog.Title>Patients who cannot receive survey texts</Dialog.Title>
          <Dialog.Description size="2" mb="3">
            Collect a working mobile number for each patient, or set their preferred contact
            method to Voice AI. Either one removes the patient from this list. Saving a new number
            resumes texts automatically.
          </Dialog.Description>
          <Table.Root size="1" variant="surface">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell>Patient</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>MRN</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Phone</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Problem</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Since</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Surveys</Table.ColumnHeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {patients.map((p) => (
                <Table.Row
                  key={p.patient_id}
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    setOpen(false);
                    onOpenPatient(p);
                  }}
                >
                  <Table.Cell>
                    {[p.last_name, p.first_name].filter(Boolean).join(', ') || '—'}
                  </Table.Cell>
                  <Table.Cell>{p.patient_id}</Table.Cell>
                  <Table.Cell>{p.phone_number || '—'}</Table.Cell>
                  <Table.Cell>
                    <Text size="1">{p.error_label || 'Undeliverable'}</Text>
                  </Table.Cell>
                  <Table.Cell>{formatSince(p.suppressed_since)}</Table.Cell>
                  <Table.Cell>
                    {p.surveys_route_to_voice ? (
                      <Badge color="blue">Voice AI</Badge>
                    ) : (
                      <Badge color="red">Paused</Badge>
                    )}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
          <Flex justify="end" mt="3">
            <Dialog.Close>
              <Button variant="soft" color="gray">
                Close
              </Button>
            </Dialog.Close>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>
    </>
  );
};

export default SmsUndeliverableBanner;
